import { useState, type JSX } from 'react';
import { z } from 'zod';
import {
  classificationLabelsSchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, request, savedSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import type { Copy } from './copy';

const flags = ['paymentClaim', 'dispute', 'stop', 'needsHuman'] as const;
const schema = z.object({
  scorecard: z.object({
    conversations: z.number(),
    reviewedMessages: z.number(),
    passed: z.boolean(),
    signals: z.array(
      z.object({
        signal: z.enum(flags),
        positives: z.number(),
        falsePositives: z.number(),
        falseNegatives: z.number(),
        passed: z.boolean(),
        required: z.boolean(),
      })
    ),
  }),
  pending: z.array(
    z.object({ id: z.string(), conversationId: z.string(), text: z.string() })
  ),
});
const labelKeys = {
  paymentClaim: 'qualityPaymentClaim',
  dispute: 'qualityDispute',
  stop: 'qualityStop',
  needsHuman: 'qualityHuman',
} as const;

export function Quality({
  scope,
  session,
  copy,
  canWrite,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  canWrite: boolean;
}): JSX.Element {
  const result = useRemote(botPath(scope, 'quality'), schema);
  return (
    <section className="settings-page">
      <h2>{copy.quality}</h2>
      <p>{copy.qualityHelp}</p>
      <ErrorNotice error={result.error} copy={copy} onRetry={result.reload} />
      {result.loading && !result.data && <p>{copy.loading}</p>}
      {result.data && (
        <>
          <p>
            {result.data.scorecard.passed
              ? copy.qualityPassed
              : copy.qualityReviewRequired}
          </p>
          <p>
            {copy.qualityConversations}: {result.data.scorecard.conversations} ·{' '}
            {copy.qualityMessages}: {result.data.scorecard.reviewedMessages}
          </p>
          <table>
            <thead>
              <tr>
                <th>{copy.qualitySignal}</th>
                <th>{copy.qualityRequirement}</th>
                <th>{copy.qualityPositive}</th>
                <th>{copy.qualityFalsePositive}</th>
                <th>{copy.qualityMissed}</th>
              </tr>
            </thead>
            <tbody>
              {result.data.scorecard.signals.map((signal) => (
                <tr key={signal.signal}>
                  <td>{copy[labelKeys[signal.signal]]}</td>
                  <td>
                    {signal.required
                      ? copy.qualityRequired
                      : copy.qualityMeasuredOnly}
                  </td>
                  <td>{signal.positives}</td>
                  <td>{signal.falsePositives}</td>
                  <td>{signal.falseNegatives}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canWrite &&
            result.data.pending.map((item) => (
              <ClassificationReview
                key={item.id}
                item={item}
                scope={scope}
                session={session}
                copy={copy}
                reload={result.reload}
              />
            ))}
        </>
      )}
    </section>
  );
}

export function ClassificationReview({
  item,
  scope,
  session,
  copy,
  reload,
  reviewRoute = 'quality',
}: {
  reviewRoute?: string;
  item: z.infer<typeof schema>['pending'][number];
  scope: BotScope;
  session: Session;
  copy: Copy;
  reload: () => void;
}): JSX.Element {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const mutation = useMutation();
  return (
    <form
      className="inspector-section"
      onSubmit={(event) =>
        onSubmit(event, () =>
          mutation.run(async () => {
            const selected = z
              .record(z.enum(flags), z.enum(['yes', 'no']))
              .parse(answers);
            const labels = classificationLabelsSchema.parse(
              Object.fromEntries(
                flags.map((flag) => [flag, selected[flag] === 'yes'])
              )
            );
            await request(botPath(scope, reviewRoute), savedSchema, {
              csrf: session.csrf,
              body: { eventId: item.id, labels },
            });
            reload();
          })
        )
      }
    >
      <p className="prewrap">{item.text}</p>
      {flags.map((flag) => (
        <Field key={flag} label={copy[labelKeys[flag]]}>
          <select
            required
            value={answers[flag] ?? ''}
            onChange={(event) =>
              setAnswers((previous) => ({
                ...previous,
                [flag]: event.target.value,
              }))
            }
          >
            <option value="">{copy.selectAnswer}</option>
            <option value="yes">{copy.yes}</option>
            <option value="no">{copy.no}</option>
          </select>
        </Field>
      ))}
      <ErrorNotice error={mutation.error} copy={copy} />
      <button disabled={mutation.busy}>{copy.reviewApprove}</button>
    </form>
  );
}
